import React, { useState } from 'react';
import queryString from 'query-string';
import { FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react';
import { withRouter, RouteComponentProps } from 'react-router';

import SVGIcon from 'lib/images/svg-icon/svg-icon';
import Styled from 'styled';

import pricetag from 'four-nets/icons/svg/icon_price.svg';
import message from 'four-nets/icons/svg/icon_comment.svg';
import eye from 'four-nets/icons/svg/icon_views.svg';
import trash from 'four-nets/icons/svg/icon_delete.svg';
import clock from 'four-nets/icons/svg/icon_activity2.svg';
import rightToUp from 'four-nets/icons/svg/icon_right-to-up.svg';
import users from 'four-nets/icons/svg/icon_users.svg';

import PagerView from 'four-nets/ui/pager-view';
import LoadingWrapperView from 'four-nets/ui/loaders/loading-wrapper-view';

import ProductsTableRowView from './products-table-row-view/products-table-row-view';
import { MyProductsQuery } from '../query.graphql';
import defaultStyles from './products-table-view.scss';
import rightToUpStyles from './products-table-view-righttoup.scss';
import trashStyles from './products-table-view-trash.scss';

export interface Props extends RouteComponentProps {
    styles: typeof defaultStyles;
    ascending?: boolean;
    sortable?: boolean;
    loading: boolean;
    myBazaarType: string;
    page: number;
    pageSize: number;
    data: MyProductsQuery;

    onSubmit: (selectedIds: string[]) => void;
}

const ProductsTableView: React.FC<Props> = (props: Props) => {
    const { myBazaarType, history, location, page, pageSize, loading, sortable = true, data } = props;

    const [values, setValues] = useState(new Map());
    const styles = props.styles!;

    const products = data && data.productsDb && data.productsDb.edges;
    const totalCount = data && data.productsDb && data.productsDb.totalCount;

    const changeSort = (_: React.ChangeEvent<HTMLSelectElement>, value: string) => {
        if (!sortable) {
            return;
        }
        history.push({
            pathname: location.pathname,
            search: `?${queryString.stringify({
                ...queryString.parse(location.search),
                page: 1,
                asc: value,
            })}`,
        });
    };

    const handleChange = (target: HTMLInputElement) => {
        values.set(target.value, target.checked);
        setValues(values);
    };

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const selectedIds: string[] = [];
        values.forEach((value, key) => {
            if (value) {
                selectedIds.push(key);
            }
        });
        props.onSubmit(selectedIds);
    };

    return (
        <div className={styles.pageWrapper}>
            <LoadingWrapperView loading={loading}>
                <form onSubmit={onSubmit} method='post'>
                    <table className={styles.table}>
                        <thead className={styles.tableHead}>
                            <tr className={styles.tableRow}>
                                <th className={styles.checkbox} />
                                <th className={styles.noWidth} />
                                <th className={styles.tableCellHeadingName}>
                                    <FormattedMessage id='BAZAAR_TABLE_HEADING_NAME' />
                                </th>
                                <th className={styles.tableCellHeading}>
                                    <SVGIcon style={{ image: styles.icon }} src={pricetag} />
                                </th>
                                <th className={styles.tableCellHeading}>
                                    <SVGIcon
                                        style={{ image: styles.icon }}
                                        src={myBazaarType !== 'wishlist' ? message : users}
                                    />
                                </th>
                                <th className={styles.tableCellHeading}>
                                    <SVGIcon style={{ image: styles.icon }} src={eye} />
                                </th>
                                <th className={styles.tableCellHeading}>
                                    <SVGIcon style={{ image: styles.icon }} src={clock} />
                                </th>
                                {myBazaarType === 'selling' ? <th className={styles.repost} /> : null}
                            </tr>
                        </thead>
                        <tbody className={styles.tableBody}>
                            {products &&
                                products
                                    .filter(x => x && x.node)
                                    .map(edge => {
                                        const product = edge!.node!;
                                        return (
                                            <ProductsTableRowView
                                                {...product}
                                                myBazaarType={myBazaarType}
                                                key={product.id}
                                                checked={values.has(product.id) && values.get(product.id)}
                                                handleChange={handleChange}
                                            />
                                        );
                                    })}
                        </tbody>
                    </table>
                    {products && products.length === 0 ? (
                        <div className={styles.emptyTable}>
                            <FormattedMessage id='BAZAAR_NO_PRODUCTS' />
                        </div>
                    ) : (
                        <div className={styles.submitBar}>
                            <SVGIcon style={{ image: rightToUpStyles.rightToUp }} src={rightToUp} />
                            <SVGIcon style={{ image: trashStyles.trash }} src={trash} />
                            <button className={styles.confirm} type='submit'>
                                <FormattedMessage id='BAZAAR_DELETE_SELECTED_PRODUCTS'>
                                    {message1 => (
                                        <span>
                                            {message1}&nbsp;
                                            {myBazaarType === 'shopping' ? (
                                                <FormattedMessage id='BAZAAR_UNTIL_NEW_MESSAGES_NOTE'>
                                                    {message2 => <span className={styles.note}>({message2})</span>}
                                                </FormattedMessage>
                                            ) : null}
                                        </span>
                                    )}
                                </FormattedMessage>
                            </button>
                            &nbsp;
                        </div>
                    )}
                </form>
            </LoadingWrapperView>
            {totalCount && totalCount > 1 ? (
                <div className={styles.pagerWrapper}>
                    <PagerView count={Math.ceil(totalCount / pageSize)} current={page} />
                </div>
            ) : null}
        </div>
    );
};

export default Styled(defaultStyles)(withRouter(ProductsTableView));
